import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import services, { getServiceBySlug } from '../data/services';
import PageHero from '../components/PageHero';
import { heroSubtitleClass, heroTitleClass, motionSafe } from '../constants/heroTypography';
import carousel2 from '../assets/img/gallery/carousel/2.jpeg';
import carousel3 from '../assets/img/gallery/carousel/3.jpeg';
import carousel5 from '../assets/img/gallery/carousel/5.jpeg';
import carousel11 from '../assets/img/gallery/carousel/11.jpeg';
import carousel12 from '../assets/img/gallery/carousel/12.jpeg';

const surfaceCard = 'rounded-2xl border border-slate-200 bg-white shadow-md';

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gold/15">
        <svg className="h-3.5 w-3.5 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
        </svg>
      </span>
      <span className="text-sm leading-relaxed text-navy/80">{children}</span>
    </li>
  );
}

function SectionRule() {
  return <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/35 to-transparent" aria-hidden />;
}

/** Medical Tourism route only: replace spaced brand in plain strings. */
function replaceRayanStarBrand(s) {
  return typeof s === 'string' ? s.replace(/RAYANSTAR/g, 'RAYAN STAR') : s;
}

/** Medical Tourism page only: ensure copy uses RAYAN STAR (no space). */
function normalizeMedicalBrandText(value) {
  if (typeof value === 'string') {
    return value.replace(/RAYAN STAR/g, 'RAYANSTAR');
  }
  if (Array.isArray(value)) {
    return value.map(normalizeMedicalBrandText);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, normalizeMedicalBrandText(v)]));
  }
  return value;
}

function MedicalIndiaDetail({ data }) {
  const d = useMemo(() => normalizeMedicalBrandText(data), [data]);

  return (
    <div className="space-y-5">
      {d.introParagraphs.map((p, i) => (
        <p key={i} className="text-left text-base leading-relaxed text-navy/75">
          {p}
        </p>
      ))}

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Why choose India for medical treatment?</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {d.whyIndia.map((item) => (
            <div key={item.title} className={`${surfaceCard} p-4 sm:p-5`}>
              <p className="text-sm font-semibold text-gold">
                <span className="mr-1.5 text-gold" aria-hidden>
                  ✔
                </span>
                {item.title}
              </p>
              <p className="mt-5 text-sm leading-relaxed text-navy/75">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Our complete medical support services</h2>
        <p className="mt-5 text-left text-sm leading-relaxed text-navy/75">{d.supportIntro}</p>
        <ul className="mt-5 space-y-3">
          {d.supportItems.map((t) => (
            <Bullet key={t}>{t}</Bullet>
          ))}
        </ul>
      </section>

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Quality nursing &amp; patient care</h2>
        <p className="mt-5 text-left text-sm leading-relaxed text-navy/75">{d.nursingIntro}</p>
        <ul className="mt-5 space-y-3">
          {d.nursingItems.map((t) => (
            <Bullet key={t}>{t}</Bullet>
          ))}
        </ul>
      </section>

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Popular treatments available</h2>
        <ul className="mt-5 space-y-3">
          {d.popularTreatments.map((t) => (
            <Bullet key={t}>{t}</Bullet>
          ))}
        </ul>
      </section>

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Traditional &amp; holistic healing</h2>
        <p className="mt-5 text-left text-sm leading-relaxed text-navy/75">{d.holisticParagraph}</p>
      </section>

      <SectionRule />

      <section>
        <h2 className="text-left text-base font-semibold text-navy/90">Why choose RAYAN STAR Consultancy?</h2>
        <ul className="mt-5 space-y-3">
          {d.whyChooseUs.map((t) => (
            <Bullet key={t}>{t}</Bullet>
          ))}
        </ul>
      </section>

      <SectionRule />

      <section className={`${surfaceCard} bg-gradient-to-br from-gold/10 to-white p-6 sm:p-7`}>
        <h2 className="text-center text-base font-bold uppercase tracking-[0.2em] text-navy">{d.contactCta.headline}</h2>
        <div className="mt-5 space-y-5 text-center">
          <p className="text-sm leading-relaxed text-navy/80">{d.contactCta.subline}</p>
          <p className="font-semibold text-navy">{d.contactCta.companyLine}</p>
          <p className="text-sm text-navy/75">
            <span aria-hidden>📍 </span>
            {d.contactCta.location}
          </p>
          <p className="text-sm text-navy/75">
            <span aria-hidden>📱 </span>
            <a href={`tel:${d.contactCta.phoneTel}`} className="font-semibold text-gold hover:underline">
              {d.contactCta.phoneDisplay}
            </a>
          </p>
        </div>
        <div className="mt-5 flex justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-md bg-gold px-8 py-3 text-sm font-semibold text-navy transition hover:bg-gold-light"
          >
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function ServiceDetails() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const talentCarousel = useMemo(
    () => [carousel2, carousel3, carousel5, carousel11, carousel12],
    []
  );
  const [talentIdx, setTalentIdx] = useState(0);

  useEffect(() => {
    setTalentIdx(0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-navy-950">
        <div className="mx-auto max-w-3xl px-4 pb-16 pt-32 sm:px-6 lg:px-8">
          <h1 className="font-sans text-3xl font-bold text-white">Service not found</h1>
          <p className="mt-4 text-white/70">Please go back to the services page.</p>
        </div>
      </div>
    );
  }

  const isMedicalTourism = service.slug === 'medical-tourism';
  const heroTitle = isMedicalTourism ? replaceRayanStarBrand(service.title) : service.title;
  const heroSubtitle = isMedicalTourism ? replaceRayanStarBrand(service.short) : service.short;

  return (
    <div className="bg-navy-950">
      <PageHero
        title={heroTitle}
        subtitle={heroSubtitle}
        backgroundImage={service.heroBg}
        titleClassName={heroTitleClass}
        subtitleClassName={heroSubtitleClass(motionSafe)}
      />

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-10 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
            <div className={`${surfaceCard} p-6 sm:p-7`}>
              {service.detailLayout === 'medical-india' && service.medicalIndia ? (
                <MedicalIndiaDetail data={service.medicalIndia} />
              ) : (
                <>
                  <p className="text-justify text-base leading-relaxed text-navy/75">{service.intro}</p>

                  <div className="mt-10 grid gap-6 sm:grid-cols-2">
                    <section className={`${surfaceCard} p-5`}>
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-navy/80">
                        Our experienced team assists with
                      </h2>
                      <div className="mt-3 h-px w-full bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
                      <ul className="mt-5 space-y-3">
                        {service.assistsWith.map((t) => (
                          <Bullet key={t}>{t}</Bullet>
                        ))}
                      </ul>
                    </section>

                    <section className={`${surfaceCard} p-5`}>
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-navy/80">
                        Clients benefit from
                      </h2>
                      <div className="mt-3 h-px w-full bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
                      <ul className="mt-5 space-y-3">
                        {service.benefits.map((t) => (
                          <Bullet key={t}>{t}</Bullet>
                        ))}
                      </ul>
                    </section>
                  </div>

                  <p className="mt-10 text-justify text-sm leading-relaxed text-navy/70">{service.closing}</p>
                </>
              )}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              {service.slug === 'talent-acquisition' ? (
                <div className={`overflow-hidden ${surfaceCard} ring-1 ring-slate-200`}>
                  <div className="relative bg-black/10">
                    <img
                      src={talentCarousel[talentIdx]}
                      alt={`${service.title} — ${talentIdx + 1}`}
                      className="w-full object-contain"
                    />

                    <button
                      type="button"
                      onClick={() => setTalentIdx((i) => (i - 1 + talentCarousel.length) % talentCarousel.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/30 text-white/90 shadow-lg shadow-black/40 transition hover:bg-black/45"
                      aria-label="Previous image"
                    >
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTalentIdx((i) => (i + 1) % talentCarousel.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/30 text-white/90 shadow-lg shadow-black/40 transition hover:bg-black/45"
                      aria-label="Next image"
                    >
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-2 bg-white px-4 py-3" aria-hidden>
                    {talentCarousel.map((_, i) => (
                      <span
                        key={i}
                        className={`h-2.5 w-2.5 rounded-full ring-1 transition ${
                          i === talentIdx ? 'bg-gold ring-gold/40' : 'bg-slate-300 ring-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`overflow-hidden ${surfaceCard} ring-1 ring-slate-200`}>
                  <img
                    src={service.detailImage}
                    alt={isMedicalTourism ? replaceRayanStarBrand(service.title) : service.title}
                    className="w-full object-contain"
                  />
                </div>
              )}

              <div className="mt-6 grid gap-3">
                <div className={`${surfaceCard} p-5`}>
                  <p className="text-xs font-semibold tracking-[0.25em] text-gold">READY TO START?</p>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    Talk to our team for a fast, clear, and reliable process.
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy transition hover:bg-gold-light"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* CTA band */}
      <section className="bg-navy-950 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-6 lg:px-8">
          <div className="text-center sm:text-left">
            <h2 className="font-sans text-2xl font-bold text-white">
              Let Us Handle Your {isMedicalTourism ? replaceRayanStarBrand(service.title) : service.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-white/75">
              Experience a smooth and hassle-free process with our expert support.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy transition hover:bg-gold-light"
          >
            Get Started Today
          </Link>
        </div>
      </section>

      {/* Bottom navigation */}
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex flex-wrap gap-2">
            {services.slice(0, 5).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  s.slug === service.slug
                    ? 'bg-gold text-navy'
                    : 'bg-navy-800 text-white/80 hover:bg-navy-700'
                }`}
              >
                {s.title.replace(' Services', '').replace(' Service', '')}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

